import React, { lazy, Suspense, useEffect } from 'react';
import RouteFallback from './components/RouteFallback';
import { BrowserRouter as Router, Route, Routes, Navigate } from 'react-router-dom';
import './App.css';
import AddTournamentWizard from './components/AddTournamentWizard';
import NewScorecardEntry from './NewScorecardEntry';
import PlayerManager from './PlayerManager';
import CourseManager from './CourseManager';
import TournamentManager from './TournamentManager';
import Groups from './Groups';
import AdaptiveHome from './pages/AdaptiveHome';
import Login from './Login';
import LegalPage from './LegalPage';
import SupportPage from './SupportPage';
import InsightsPage from './InsightsPage';
import PredictionsPage from './predictions/PredictionsPage';
import HandicapAdjustmentManager from './HandicapAdjustmentManager';
import Setup from './Setup';
import SocietyHub from './SocietyHub';
import ProfilePage from './ProfilePage';
import JoinSociety from './JoinSociety';
import AdminDashboard from './AdminDashboard';
import CourseDownloader from './CourseDownloader';
import PromptLab from './PromptLab';

import { AuthProvider, useAuth } from './AuthContext';
import PushBridge from './notifications/PushBridge';
import InAppNotificationPill from './components/InAppNotificationPill';
import { NetworkProvider } from './NetworkContext';
import OfflineModeBanner from './components/OfflineModeBanner';
import OfflineGate from './components/OfflineGate';
import UpdateBanner from './components/UpdateBanner';
import TermsAcceptanceGate from './components/TermsAcceptanceGate';
import { SubscriptionProvider } from './SubscriptionContext';
import SubscriptionGate from './components/SubscriptionGate';

import { ToastContainer } from 'react-toastify';
import { pillTransition } from './utils/notify';
import ErrorBoundary from './components/ErrorBoundary';

import 'react-toastify/dist/ReactToastify.css';

import { QueryClientProvider } from 'react-query';
import queryClient from './queryClient';
import { startSyncProcessor } from './syncProcessor';
import { setSyncOwner } from './offlineDb';
import { useApi } from './useApi';

const Leaderboard = lazy(() => import('./Leaderboard'));
const TeeSheet = lazy(() => import('./TeeSheet'));
const ScorecardInput = lazy(() => import('./ScorecardInput'));
const LiveScoring = lazy(() => import('./LiveScoring'));
const RoundStatsPage = lazy(() => import('./RoundStatsPage'));
const MyStatsPage = lazy(() => import('./MyStatsPage'));
const NotificationSettingsPage = lazy(() => import('./NotificationSettingsPage'));
const TeamLiveScoring = lazy(() => import('./TeamLiveScoring'));
const MatchplayLeaderboard = lazy(() => import('./MatchplayLeaderboard'));
const PlayHub = lazy(() => import('./PlayHub'));
const SocietyMemberManager = lazy(() => import('./SocietyMemberManager'));
const SocietyActivityPage = lazy(() => import('./SocietyActivityPage'));
const OrderOfMeritManager = lazy(() => import('./OrderOfMeritManager'));
const OrderOfMeritLeaderboard = lazy(() => import('./OrderOfMeritLeaderboard'));
const CompetitionLeaderboard = lazy(() => import('./CompetitionLeaderboard'));
const CompetitionBuilder = lazy(() => import('./CompetitionBuilder'));
const AdvancedCompetitionWizard = lazy(() => import('./AdvancedCompetitionWizard'));
const PresentationView = lazy(() => import('./PresentationView'));
// Protected Route Component
const ProtectedRoute = ({ children }) => {
  const { currentUser, authInitialized } = useAuth();
  if (!authInitialized) return null;
  if (!currentUser) {
    return <Navigate to="/login" />;
  }
  return children;
};

// Requires the user to belong to at least one society
const RequireSociety = ({ children }) => {
  const { userProfile, isProfileLoading, isViewingAsAdmin } = useAuth();
  if (isProfileLoading) return null;
  // A system admin opening a society they are not a member of HAS a society —
  // it just isn't one of theirs. Gating on the membership list alone bounced
  // them straight back to /home from every screen they were trying to support.
  if (!userProfile?.societies?.length && !isViewingAsAdmin) {
    return <Navigate to="/home" />;
  }
  return children;
};

/**
 * Drains the offline write queue for the whole app, wherever the player is.
 *
 * It used to be started by the two live-scoring screens only, which meant a
 * write queued from anywhere else — the standalone round-stats screen most of
 * all, where a finalised round gets its stats filled in afterwards — had
 * nothing listening for it and never reached the server. A queue is only as
 * good as the thing draining it, and that thing belongs at the top of the app,
 * not on the screens that happen to write most often.
 *
 * `currentUser`, not `authInitialized`: `authenticatedFetch` throws without a
 * user, and a doomed attempt here would burn a retry against the backoff.
 */
const SyncProcessor = () => {
  const { authenticatedFetch } = useApi();
  const { currentUser } = useAuth();
  useEffect(() => {
    // Before the processor starts, so the first drain is already scoped to
    // this user's writes (see `setSyncOwner` in offlineDb.js).
    setSyncOwner(currentUser ? currentUser.uid : null);
    if (!currentUser) return undefined;
    return startSyncProcessor(authenticatedFetch);
  }, [authenticatedFetch, currentUser]);
  return null;
};

// The app-wide drawer used to live here. Navigation is now the bottom nav for
// the four roots, a back arrow in PageHeader for everything below them, and the
// avatar for account screens — one way to each place instead of two.
const Layout = ({ children }) => <>{children}</>;

function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <NetworkProvider>
      <AuthProvider>
        <SubscriptionProvider>
        <Router>
          <div className="App">
            <SyncProcessor />
            <PushBridge />
            <TermsAcceptanceGate>
            <SubscriptionGate>
            <Layout>
              <UpdateBanner />
              <OfflineModeBanner />
              <ToastContainer
                position="top-center"
                limit={2}
                newestOnTop
                hideProgressBar
                closeButton={false}
                closeOnClick
                draggable
                draggablePercent={40}
                transition={pillTransition}
              />
              <InAppNotificationPill />
              <ErrorBoundary>
              <Suspense fallback={<RouteFallback />}>
                <Routes>
                  {/* Un-gated routes (work offline) */}
                  <Route path="/" element={<AdaptiveHome />} />
                  <Route path="/home" element={<AdaptiveHome />} />
                  <Route path="/login" element={<Login />} />
                  <Route path="/join" element={<JoinSociety />} />
                  <Route path="/legal" element={<LegalPage />} />
                  <Route path="/support" element={<SupportPage />} />
                  <Route path="/terms" element={<Navigate to="/legal?tab=terms" replace />} />
                  <Route path="/privacy" element={<Navigate to="/legal?tab=privacy" replace />} />
                  <Route path="/live-scoring/:tournamentId/:groupId" element={<LiveScoring />} />
                  {/* One round's stats on their own — the way back to them once
                      the round is finalised and the scoring page is closed.
                      Self-only: the endpoint behind it 403s anyone else's round,
                      and the page carries no route to the scores. */}
                  <Route path="/my-stats" element={<OfflineGate><ProtectedRoute><RequireSociety><MyStatsPage /></RequireSociety></ProtectedRoute></OfflineGate>} />
                  {/* What the logging adds up to. Self-only like everything on
                      this path — the endpoint behind it takes no player id. */}
                  {/* One department in full — the drawing, the breakdown and
                      the decision. Same window as the page behind it, carried
                      in `?w=` so a back arrow lands where it left. */}
                  <Route path="/rounds/:roundId/stats" element={<OfflineGate><ProtectedRoute><RequireSociety><RoundStatsPage /></RequireSociety></ProtectedRoute></OfflineGate>} />
                  <Route path="/team-scoring/:tournamentId/:teamRoundId" element={<TeamLiveScoring />} />

                  {/* Online-only protected routes */}
                  <Route path="/leaderboard" element={<OfflineGate><ProtectedRoute><RequireSociety><Leaderboard /></RequireSociety></ProtectedRoute></OfflineGate>} />
                  <Route path="/play" element={<OfflineGate><ProtectedRoute><RequireSociety><PlayHub /></RequireSociety></ProtectedRoute></OfflineGate>} />
                  <Route path="/players" element={<OfflineGate><ProtectedRoute><RequireSociety><PlayerManager /></RequireSociety></ProtectedRoute></OfflineGate>} />
                  <Route path="/courses" element={<OfflineGate><ProtectedRoute><CourseManager /></ProtectedRoute></OfflineGate>} />
                  <Route path="/tournaments" element={<OfflineGate><ProtectedRoute><RequireSociety><TournamentManager /></RequireSociety></ProtectedRoute></OfflineGate>} />
                  <Route path="/tournaments/add" element={<OfflineGate><ProtectedRoute><RequireSociety><AddTournamentWizard /></RequireSociety></ProtectedRoute></OfflineGate>} />
                  <Route path="/tournaments/:tournamentId/groups" element={<OfflineGate><ProtectedRoute><RequireSociety><Groups /></RequireSociety></ProtectedRoute></OfflineGate>} />
                  <Route path="/tournaments/scorecard-entry" element={<OfflineGate><ProtectedRoute><RequireSociety><NewScorecardEntry /></RequireSociety></ProtectedRoute></OfflineGate>} />
                  {/* Group picker stays reachable offline so scores can still be entered */}
                  <Route path="/tournaments/:tournamentId/scorecard-input" element={<ProtectedRoute><RequireSociety><ScorecardInput /></RequireSociety></ProtectedRoute>} />
                  <Route path="/tournaments/:tournamentId/teesheet" element={<OfflineGate><ProtectedRoute><RequireSociety><TeeSheet /></RequireSociety></ProtectedRoute></OfflineGate>} />
                  <Route path="/insights" element={<OfflineGate><ProtectedRoute><RequireSociety><InsightsPage /></RequireSociety></ProtectedRoute></OfflineGate>} />
                  <Route path="/betting" element={<OfflineGate><ProtectedRoute><RequireSociety><PredictionsPage /></RequireSociety></ProtectedRoute></OfflineGate>} />
                  {/* The market book is now the Predictions page's Book tab. This
                      route kept a second copy of it alive beside the Head to Head
                      screen that page replaced, and nothing had linked to it since
                      Predictions took over `/betting` — so the only way in was to
                      type the URL. Redirect rather than 404 any bookmark. */}
                  <Route path="/betting/book" element={<Navigate to="/betting" replace />} />
                  <Route path="/handicap-adjustments" element={<OfflineGate><ProtectedRoute><RequireSociety><HandicapAdjustmentManager /></RequireSociety></ProtectedRoute></OfflineGate>} />
                  <Route path="/order-of-merit" element={<OfflineGate><ProtectedRoute><RequireSociety><OrderOfMeritLeaderboard /></RequireSociety></ProtectedRoute></OfflineGate>} />
                  <Route path="/order-of-merit/manage" element={<OfflineGate><ProtectedRoute><RequireSociety><OrderOfMeritManager /></RequireSociety></ProtectedRoute></OfflineGate>} />
                  <Route path="/order-of-merit/:id" element={<OfflineGate><ProtectedRoute><RequireSociety><OrderOfMeritLeaderboard /></RequireSociety></ProtectedRoute></OfflineGate>} />
                  <Route path="/tournaments/:tournamentId/matchplay-leaderboard" element={<OfflineGate><MatchplayLeaderboard /></OfflineGate>} />
                  <Route path="/tournaments/:tournamentId/competitions" element={<OfflineGate><ProtectedRoute><RequireSociety><CompetitionLeaderboard /></RequireSociety></ProtectedRoute></OfflineGate>} />
                  <Route path="/tournaments/:tournamentId/competitions/manage" element={<OfflineGate><ProtectedRoute><RequireSociety><CompetitionBuilder /></RequireSociety></ProtectedRoute></OfflineGate>} />
                  <Route path="/tournaments/add-advanced" element={<OfflineGate><ProtectedRoute><RequireSociety><AdvancedCompetitionWizard /></RequireSociety></ProtectedRoute></OfflineGate>} />
                  <Route path="/setup" element={<OfflineGate><ProtectedRoute><Setup /></ProtectedRoute></OfflineGate>} />
                  <Route path="/society" element={<OfflineGate><ProtectedRoute><RequireSociety><SocietyHub /></RequireSociety></ProtectedRoute></OfflineGate>} />
                  <Route path="/societies/:societyId/members" element={<OfflineGate><ProtectedRoute><RequireSociety><SocietyMemberManager /></RequireSociety></ProtectedRoute></OfflineGate>} />
                  <Route path="/societies/:societyId/activity" element={<OfflineGate><ProtectedRoute><RequireSociety><SocietyActivityPage /></RequireSociety></ProtectedRoute></OfflineGate>} />
                  <Route path="/profile" element={<OfflineGate><ProtectedRoute><ProfilePage /></ProtectedRoute></OfflineGate>} />
                  <Route path="/profile/notifications" element={<OfflineGate><ProtectedRoute><NotificationSettingsPage /></ProtectedRoute></OfflineGate>} />
                  <Route path="/presentation/:tournamentId" element={<OfflineGate><ProtectedRoute><RequireSociety><PresentationView /></RequireSociety></ProtectedRoute></OfflineGate>} />
                  <Route path="/admin" element={<OfflineGate><ProtectedRoute><AdminDashboard /></ProtectedRoute></OfflineGate>} />
                  <Route path="/course-downloader" element={<OfflineGate><ProtectedRoute><CourseDownloader /></ProtectedRoute></OfflineGate>} />
                  <Route path="/course-downloader/prompts" element={<OfflineGate><ProtectedRoute><PromptLab /></ProtectedRoute></OfflineGate>} />
                </Routes>
              </Suspense>
              </ErrorBoundary>
            </Layout>
            </SubscriptionGate>
            </TermsAcceptanceGate>
          </div>
        </Router>
        </SubscriptionProvider>
      </AuthProvider>
      </NetworkProvider>
    </QueryClientProvider>
  );
}

export default App;